import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import BasicModal from './BasicModal'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { TextButtonVariant } from '../Buttons/TextButton'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { useFetchPlaylists } from '../../hooks/useFetchPlaylists'
import { FetchUserPlaylistsPlaylistTypeEnum as PlaylistTypeEnum } from '../../swagger'
import EmptyPlaylist from '../Playlist/EmptyPlaylist'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { playlistApi } from '../../api/swagger'
import { CreatePlaylistRequestBodyPlaylistTypeEnum } from '../../swagger'
import {
  MAX_PLAYLISTS_PER_USER,
  PLAYLIST_TEXT_TRUNCATION_LIMIT_MODAL,
} from '../../utils/constants'

interface AddAssetToPlaylistModalProps {
  isOpen: boolean
  onClose: () => void
  assetKeys: number[]
  playlistType: PlaylistTypeEnum
  isLearningPath?: boolean
}
export const successMessage =
  'The selected content has been successfully added to the playlist.'

const AddAssetToPlaylistModal: React.FC<AddAssetToPlaylistModalProps> = ({
  isOpen = false,
  onClose,
  playlistType,
  assetKeys,
  isLearningPath = false,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { handleMessage, handleError } = useAppSnackbar()

  const {
    Playlist: {
      createPlaylist: createPlaylistLoadingId,
      createPlaylistItems: createPlaylistItemsLoadingId,
    },
  } = useLoadingIds()

  const { playlistsContent, triggerFetch, isLoading } = useFetchPlaylists({
    playlistType,
    include: 'owned',
  })

  const [playlistNameValidation, setPlaylistNameValidation] = useState<{
    isError: boolean
    message: React.JSX.Element | string
    isDisabled?: boolean
    isAvailableSpot?: boolean
  }>({
    isError: false,
    message: '',
    isAvailableSpot: true,
  })

  const [selectedPlaylist, setSelectedPlaylist] = useState<number>()
  const [playlistName, setPlaylistName] = useState('')

  /**  Used to store the new playlistKey temporarily for Learning Paths */
  const playlistKeyForLP = useRef<number>()

  const validationLimitItem = useCallback(() => {
    const totalRecords = playlistsContent?.totalCount ?? 0
    const validRecord =
      totalRecords >= MAX_PLAYLISTS_PER_USER && !selectedPlaylist
    if (validRecord) {
      const message = t(
        'AddAssetToPlaylistModal.Error.MaxLimit',
        `You've reached the maximum of {{maxLimit}} playlists. To create a new one, you'll need to delete an existing playlist first.`,
        { maxLimit: MAX_PLAYLISTS_PER_USER }
      )
      updatePlaylistNameValidation({
        isError: true,
        message,
        isDisabled: true,
        isAvailableSpot: false,
      })
    } else {
      updatePlaylistNameValidation({
        isError: false,
        message: '',
        isAvailableSpot: true,
      })
    }
  }, [playlistsContent?.totalCount, selectedPlaylist, t])

  useEffect(() => {
    if (isOpen) {
      resetFormState()
      triggerFetch()
    }
  }, [triggerFetch, isOpen])

  useEffect(() => {
    if (isOpen && playlistsContent?.totalCount !== undefined) {
      validationLimitItem()
    }
  }, [validationLimitItem, playlistsContent?.totalCount, isOpen])

  const defaultAddPlaylistErrorMessage = t(
    'AddAssetToPlaylistModal.AddPlaylist.Error',
    'An error occurred while trying to add the asset to the selected playlist.'
  )

  /**
   * Determines if Save button should be disabled.
   * When the user has already reached the maximum playlists allowed
   * and hasn't selected any existing playlist.
   */
  const isSaveDisabled =
    (playlistsContent?.totalCount ?? 0) >= MAX_PLAYLISTS_PER_USER &&
    !selectedPlaylist

  const createPlaylistItem = async () => {
    try {
      await playlistApi.createPlaylistItems({
        body: {
          playlistKey: selectedPlaylist ?? playlistKeyForLP.current ?? 0,
          widenAssetKeys: assetKeys,
        },
      })
    } catch (error) {
      await handleError(error, defaultAddPlaylistErrorMessage)
    }
  }

  const { triggerFetch: createPlaylistItemsEndPoint } = useLoadingContext({
    asyncFunction: createPlaylistItem,
    loadingId: createPlaylistItemsLoadingId,
    callback: () => {
      resetFormState()
      if (!!playlistKeyForLP.current) {
        playlistKeyForLP.current = undefined
      }
    },
  })

  /**
   * Creates a new playlist using the API.
   *
   *  For standard playlists:
   * - Sends the name, playlist type, and a single asset key (`widenAssetKey`) as the main asset.
   *
   *  For Learning Paths (`isLearningPath = true`):
   * - The `widenAssetKey` is omitted from the request.
   * - After the playlist is created, its `playlistKey` is stored in a ref (`playlistKeyForLP`) for later use
   *   when adding multiple assets in a separate request (`createPlaylistItems`).
   *
   * 🧨 Handles any errors.
   */
  const createPlaylist = async () => {
    const widenAssetKey = isLearningPath ? undefined : assetKeys[0]
    try {
      const playlist = await playlistApi.createPlaylist({
        body: {
          name: playlistName,
          playlistType:
            playlistType as unknown as CreatePlaylistRequestBodyPlaylistTypeEnum,
          widenAssetKey,
        },
      })
      if (isLearningPath && !widenAssetKey) {
        playlistKeyForLP.current = playlist.playlistKey
      }
    } catch (error) {
      await handleError(error, defaultAddPlaylistErrorMessage)
    }
  }

  const { triggerFetch: createPlaylistEndPoint } = useLoadingContext({
    asyncFunction: createPlaylist,
    loadingId: createPlaylistLoadingId,
    /**
     *  `callback`: executed after playlist creation finishes.
     *    If `isLearningPath` is true and a playlist name was provided,
     *    it triggers `createPlaylistItemsEndPoint()` to add multiple assets.
     */
    callback: () => {
      if (isLearningPath && !!playlistName && !!playlistKeyForLP.current) {
        createPlaylistItemsEndPoint()
      }
      resetFormState()
    },
  })

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const playlistKey = Number(event.target.value)
    if (playlistKey) {
      setSelectedPlaylist(playlistKey)
    }

    setPlaylistName('')
    if (!playlistNameValidation.isDisabled) {
      updatePlaylistNameValidation({})
    }
  }
  const resetFormState = async () => {
    setPlaylistName('')
    setSelectedPlaylist(undefined)
  }

  const updatePlaylistNameValidation = ({
    isError = false,
    message = '',
    isAvailableSpot = true,
    isDisabled,
  }: {
    isError?: boolean
    message?: string
    isAvailableSpot?: boolean
    isDisabled?: boolean
  }) => {
    setPlaylistNameValidation((prev) => ({
      ...prev,
      isError,
      message,
      isAvailableSpot,
      ...(isDisabled !== undefined && { isDisabled }),
    }))
  }

  const handlePlaylistNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value
    setPlaylistName(newValue)
    if (playlistNameValidation.isError) {
      updatePlaylistNameValidation({})
    }
  }
  const handleModalSave = () => {
    if (!selectedPlaylist && !playlistName.trim()) {
      const message = t(
        'AddAssetToPlaylistModal.Error.EmptyName',
        'The playlist name cannot be empty.'
      )
      updatePlaylistNameValidation({ isError: true, message })
      return
    }
    /**  Call appropriate flow */
    !!selectedPlaylist
      ? createPlaylistItemsEndPoint()
      : createPlaylistEndPoint()
    onClose()

    handleMessage(
      t('AddAssetToPlaylistModal.Actions.AssignSuccess', `{{successMessage}}`, {
        successMessage,
      })
    )
  }

  const playlistsEmpty =
    !playlistsContent?.playlists || playlistsContent?.playlists.length == 0

  const modalForm = (
    <Box sx={{ overflowY: 'unset', width: theme.spacing(50) }}>
      {!playlistsEmpty ? (
        <Box
          sx={{
            mt: 3,
            width: '100%',
            maxHeight: 250,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          <RadioGroup
            name="playlist-selection"
            value={selectedPlaylist ?? 0}
            onChange={(e) => handleRadioChange(e)}
            sx={{
              ml: 3,
            }}
          >
            {playlistsContent?.playlists.map(({ name, playlistKey }) => (
              <FormControlLabel
                key={playlistKey}
                value={playlistKey}
                control={<Radio />}
                label={
                  name.length > PLAYLIST_TEXT_TRUNCATION_LIMIT_MODAL
                    ? `${name.slice(
                        0,
                        PLAYLIST_TEXT_TRUNCATION_LIMIT_MODAL
                      )}...`
                    : name
                }
                onDoubleClick={() => {
                  if (selectedPlaylist === playlistKey) {
                    setSelectedPlaylist(undefined)
                  }
                }}
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 1,
                  flexDirection: 'row-reverse',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: 'action.hover',
                    borderRadius: '4px',
                    padding: 1,
                  },
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      ) : (
        <Box
          sx={{
            mt: 3,
            maxHeight: 250,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyPlaylist isLoading={isLoading} />
        </Box>
      )}

      <Box
        mt={6}
        width="100%"
        pt={2}
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        gap={1}
        sx={{ maxHeight: 100 }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {t('AddAssetToPlaylistModal.section.Title', 'Create new playlist')}
        </Typography>

        <TextField
          id="playlistName"
          label={t(
            'AddAssetToPlaylistModal.Field.playlistName',
            'Playlist Name'
          )}
          error={
            playlistNameValidation.isError &&
            !((playlistsContent?.totalCount ?? 0) >= MAX_PLAYLISTS_PER_USER)
          }
          helperText={
            playlistNameValidation.message ? (
              <Typography
                variant="caption"
                sx={{
                  color: !playlistNameValidation.isAvailableSpot
                    ? theme.palette.error.main
                    : undefined,
                }}
              >
                {playlistNameValidation.message}
              </Typography>
            ) : null
          }
          name="playlistName"
          variant="filled"
          value={playlistName}
          disabled={!!selectedPlaylist || playlistNameValidation?.isDisabled}
          fullWidth
          onChange={handlePlaylistNameChange}
        />
      </Box>
    </Box>
  )

  const modalProps = {
    dialogTitle: t('AddAssetToPlaylistModal.Title', 'Add to Playlist'),
    handleFormSubmit: () => undefined,
    ariaLabel: 'Add PlayList Modal',
    dialogContent: modalForm,
    fullWidth: false,
    afterClose: () => updatePlaylistNameValidation({}),
    dialogActions: (
      <ActionButtons
        secondaryButtonLabel={ContainedButtonVariant.Save}
        disableSecondaryButton={isSaveDisabled}
        secondaryClick={handleModalSave}
        tertiaryButtonLabel={TextButtonVariant.Cancel}
        tertiaryClick={onClose}
        fullWidthButtons={false}
      />
    ),
  }

  return <BasicModal isOpen={isOpen} maxWidth="lg" {...modalProps} noValidate />
}

export default AddAssetToPlaylistModal
